import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Users, UserPlus, Mail, Shield, ShieldCheck, MoreHorizontal, Loader2,
  X, Edit2, Trash2, CheckCircle, AlertCircle, Settings, Crown, User
} from 'lucide-react';
import { PermissionsModal } from './components/PermissionsModal';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'junior_rep', label: 'Junior Rep' },
  { value: 'senior_rep', label: 'Senior Rep' },
  { value: 'sales_rep', label: 'Sales Rep (legacy)' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

const ROLE_BADGES = {
  admin: { icon: Crown, label: 'Admin', color: '#7C3AED', bg: '#F5F3FF' },
  manager: { icon: ShieldCheck, label: 'Manager', color: '#059669', bg: '#ECFDF5' },
  team_lead: { icon: Users, label: 'Team Lead', color: '#0891B2', bg: '#ECFEFF' },
  senior_rep: { icon: Shield, label: 'Senior Rep', color: '#3B82F6', bg: '#EFF6FF' },
  junior_rep: { icon: User, label: 'Junior Rep', color: '#64748B', bg: '#F1F5F9' },
  sales_rep: { icon: Shield, label: 'Sales Rep', color: '#3B82F6', bg: '#EFF6FF' },
};

const TeamManagement = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', role: 'junior_rep' });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [permissionsMember, setPermissionsMember] = useState(null);

  const fetchTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/team`, { withCredentials: true });
      setTeam(data.team || []);
    } catch (err) {
      console.error('Failed to fetch team:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  const inviteMember = async (e) => {
    e.preventDefault();
    if (!inviteData.name.trim() || !inviteData.email.trim()) return;
    
    setSubmitting(true);
    setMessage(null);
    
    try {
      const { data } = await axios.post(`${API}/api/admin/team/invite`, inviteData, { withCredentials: true });
      setMessage({
        type: 'success',
        text: `Invited ${data.name}! Temporary password: ${data.temp_password}`,
        password: data.temp_password,
      });
      setInviteData({ name: '', email: '', role: 'junior_rep' });
      fetchTeam();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to invite member',
      });
    }
    setSubmitting(false);
  };

  const updateMember = async (memberId, updates) => {
    try {
      await axios.put(`${API}/api/admin/team/${memberId}`, updates, { withCredentials: true });
      setEditingMember(null);
      fetchTeam();
    } catch (err) {
      console.error('Failed to update member:', err);
    }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/team/${memberId}`, { withCredentials: true });
      fetchTeam();
    } catch (err) {
      console.error('Failed to remove member:', err);
      alert(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const getRoleBadge = (role) => ROLE_BADGES[role] || ROLE_BADGES.junior_rep;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div data-testid="team-management">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            Team Management
          </h2>
          <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>{team.length} team members</p>
        </div>
        <Button onClick={() => setShowInvite(!showInvite)} className="btn-primary" data-testid="invite-member-btn">
          <UserPlus size={16} className="mr-2" />
          Invite Member
        </Button>
      </div>

      {/* Invite Form */}
      {showInvite && (
        <Card style={{ marginBottom: '1.25rem', border: '1px solid #E5E7EB' }}>
          <CardContent className="p-4">
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '1rem' }}>
              Invite New Team Member
            </h3>
            <form onSubmit={inviteMember}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                    Full Name
                  </label>
                  <Input
                    value={inviteData.name}
                    onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="invite-name-input"
                    style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                    Email
                  </label>
                  <Input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="invite-email-input"
                    style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                    Role
                  </label>
                  <select
                    value={inviteData.role}
                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                    data-testid="invite-role-select"
                    style={{
                      width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
                      fontSize: '0.875rem', color: 'var(--navy)', background: '#fff', height: 40,
                    }}
                  >
                    {ROLE_OPTIONS.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button type="submit" disabled={submitting} className="btn-primary" data-testid="send-invite-btn">
                  {submitting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Mail size={14} className="mr-2" />}
                  Send Invite
                </Button>
                <Button type="button" onClick={() => { setShowInvite(false); setMessage(null); }} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>

            {message && (
              <div style={{
                marginTop: '1rem', padding: '0.75rem', borderRadius: 6,
                background: message.type === 'success' ? '#ECFDF5' : '#FEF2F2',
                border: `1px solid ${message.type === 'success' ? '#10B981' : '#EF4444'}`,
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
              }}>
                {message.type === 'success' ? (
                  <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0, marginTop: 2 }} />
                ) : (
                  <AlertCircle size={16} style={{ color: '#EF4444', flexShrink: 0, marginTop: 2 }} />
                )}
                <div>
                  <p style={{ fontSize: '0.813rem', color: message.type === 'success' ? '#065F46' : '#991B1B', margin: 0 }}>
                    {message.text}
                  </p>
                  {message.password && (
                    <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                      Share this password securely with the new team member.
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Team List */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="team-table">
          <thead>
            <tr style={{ background: '#FAFBFC' }}>
              {['Member', 'Role', 'Assigned Deals', 'Status', 'Actions'].map((h, i) => (
                <th key={i} style={{
                  padding: '0.75rem 1rem', fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF',
                  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid #E5E7EB',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {team.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#9CA3AF', fontSize: '0.875rem' }}>
                  No team members found
                </td>
              </tr>
            ) : team.map((member, i) => {
              const roleBadge = getRoleBadge(member.role);
              const isActive = member.is_active !== false;
              
              return (
                <tr key={member._id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`team-row-${i}`}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', background: '#E5E7EB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.875rem', fontWeight: 600, color: '#6B7280',
                      }}>
                        {member.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--navy)', fontSize: '0.875rem' }}>{member.name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    {editingMember === member._id ? (
                      <select
                        defaultValue={member.role}
                        onChange={(e) => updateMember(member._id, { role: e.target.value })}
                        style={{
                          padding: '0.25rem 0.5rem', borderRadius: 4, border: '1px solid #E5E7EB',
                          fontSize: '0.75rem', background: '#fff',
                        }}
                      >
                        {ROLE_OPTIONS.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}
                      </select>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                        padding: '2px 8px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 600,
                        background: roleBadge.bg, color: roleBadge.color,
                      }}>
                        <roleBadge.icon size={12} />
                        {roleBadge.label}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                    {member.assigned_deals || 0} deals
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 100,
                      fontSize: '0.7rem', fontWeight: 600,
                      background: isActive ? '#ECFDF5' : '#FEF2F2',
                      color: isActive ? '#059669' : '#DC2626',
                    }}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setPermissionsMember(member)}
                        style={{
                          padding: '4px 8px', borderRadius: 4, border: '1px solid #E5E7EB',
                          background: '#F0FDFA', cursor: 'pointer', color: 'var(--teal)',
                          display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 500,
                        }}
                        data-testid={`permissions-member-${i}`}
                        title="Manage permissions"
                      >
                        <Settings size={13} /> Permissions
                      </button>
                      <button
                        onClick={() => setEditingMember(editingMember === member._id ? null : member._id)}
                        style={{
                          padding: '4px 8px', borderRadius: 4, border: '1px solid #E5E7EB',
                          background: '#fff', cursor: 'pointer', color: '#6B7280',
                        }}
                        data-testid={`edit-member-${i}`}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => removeMember(member._id)}
                        style={{
                          padding: '4px 8px', borderRadius: 4, border: '1px solid #FEE2E2',
                          background: '#FEF2F2', cursor: 'pointer', color: '#DC2626',
                        }}
                        data-testid={`remove-member-${i}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Permissions Modal */}
      {permissionsMember && (
        <PermissionsModal
          member={permissionsMember}
          onClose={() => setPermissionsMember(null)}
          onSaved={() => { setPermissionsMember(null); fetchTeam(); }}
        />
      )}
    </div>
  );
};

export default TeamManagement;
